import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Building2, Calculator } from "lucide-react";
import { AssimagraMetrics } from "../../types/assimagra/assimagra-metrics.types";
import { useLanguage } from "../../contexts/LanguageContext";
import { formatNumber, formatInteger } from "../../utils/numberFormatter";

interface MetricsCardsProps {
  metrics: AssimagraMetrics | null;
}

export const MetricsCards = ({ metrics }: MetricsCardsProps) => {
  const { t } = useLanguage();

  // Provide default values if metrics is null
  const safeMetrics = metrics || {
    companies_with_footprints: { count: 0, total_associates: 0, percentage: 0 },
    total_products_calculated: 0,
    stone_types: { limestone: 0, marble: 0, granite: 0, schist: 0, other: 0 },
    unique_stone_types_count: 0,
    latest_footprints_by_company: [],
  };

  const kpiCards = [
    {
      title: t("assimagraDashboard.metrics.companiesWithFootprints.title"),
      value: `${safeMetrics.companies_with_footprints.count}/${safeMetrics.companies_with_footprints.total_associates}`,
      subValue: `${formatNumber(safeMetrics.companies_with_footprints.percentage, 2)}% - ${t(
        "assimagraDashboard.metrics.companiesWithFootprints.subtitle"
      )}`,
      icon: Building2,
      iconColor: "text-blue-600",
      bgColor: "bg-blue-50",
    },
    {
      title: t("assimagraDashboard.metrics.totalProducts.title"),
      value: formatInteger(safeMetrics.total_products_calculated),
      subValue: "", // No subtitle - cleaner look
      icon: Calculator,
      iconColor: "text-green-600",
      bgColor: "bg-green-50",
    },
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-6 mb-8">
      {kpiCards.map((card, index) => (
        <Card key={index} className="relative overflow-hidden">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-gray-600 leading-tight">{card.title}</CardTitle>
            <div className={`${card.bgColor} ${card.iconColor} p-2 rounded-lg`}>
              <card.icon className="h-4 w-4" />
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-gray-900 mb-1">{card.value}</div>
            <p className="text-xs text-gray-500">{card.subValue}</p>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};
